import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Col, Row } from 'react-flexbox-grid';
import { Field, reduxForm } from 'redux-form';
import PickerToolbar from '@material-ui/pickers/_shared/PickerToolbar';
import ToolbarButton from '@material-ui/pickers/_shared/ToolbarButton';
import moment from 'moment';

import requestStatuses from 'modules/common/constants/requestsStatuses';
import { isCurrentLanguageRtl } from 'modules/language/selectors/language';
import { Icon, ArrowTooltip } from 'shared/components';
import { Autocomplete, Select, DateTimeInput, FormError } from 'shared/components/reduxFormComponents';
import { CancelButton, SaveButton } from 'modules/common/components/_buttons';
import { ONGOING, OPENED, CANCELED } from 'modules/common/constants/appointmentStatuses';
import { WALK_IN, SCHEDULED } from 'modules/queues/constants/queueTypes';
import { FLEXIBLE, TIME_SLOT } from 'modules/appointmentsManagement/constants/schedulingTypes';
import { OPTIONAL, DISABLED, REQUIRED } from 'modules/appointmentsManagement/constants/providerSelection';
import { appointmentFormFieldLabels } from 'modules/appointmentsManagement/constants/formFieldLabels';
import { apiErrorMessagesMap as usersApiErrorMessagesMap } from 'modules/user/constants/apiErrorCodes';
import { apiErrorMessagesMap as appointmentErrorMessagesMap } from 'modules/appointmentsManagement/constants/apiErrorCodes';
import { localErrorMessagesMap } from 'modules/appointmentsManagement/constants/localErrorCodes';
import { AppointmentAccordion, PatientDetailsForm } from './components';
import { RestrictedUserField } from '../CreateAppointmentForm/components';

import theme from './EditAppointmentForm.scss';


const errorMessagesMap = { ...appointmentErrorMessagesMap, ...usersApiErrorMessagesMap, ...localErrorMessagesMap };

const propTypes = {
  intl: PropTypes.object.isRequired,
  form: PropTypes.string.isRequired,
  appointmentHasError: PropTypes.bool,
  serviceConfig: PropTypes.object.isRequired,
  untouch: PropTypes.func.isRequired,
  providersOptions: PropTypes.array.isRequired,
  providersStatus: PropTypes.string.isRequired,
  isOngoingAppointment: PropTypes.bool.isRequired,
  fetchedBy: PropTypes.any,
  onProviderChange: PropTypes.func.isRequired,
  cantChangeProviderDueToConversationStatus: PropTypes.bool.isRequired,
  conversationTypesOptions: PropTypes.array.isRequired,
  onScheduledDateChange: PropTypes.func.isRequired,
  onMonthChange: PropTypes.func.isRequired,
  timeSlotsOptions: PropTypes.array.isRequired,
  flexibleFromSlotsOptions: PropTypes.array.isRequired,
  flexibleToSlotsOptions: PropTypes.array.isRequired,
  shouldDisableDate: PropTypes.func.isRequired,
  shouldDisableEndDate: PropTypes.func.isRequired,
  onFlexibleFromChange: PropTypes.func.isRequired,
  formatFlexibleInputChange: PropTypes.func.isRequired,
  timeSlotsStatus: PropTypes.string.isRequired,
  calendarTimeSlotsStatus: PropTypes.string.isRequired,
  formValues: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  maxDate: PropTypes.number,
  changeFieldValue: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
  appointmentStatusOptions: PropTypes.array.isRequired,
  appointmentStateOptions: PropTypes.array.isRequired,
  canEditStatus: PropTypes.bool.isRequired,
  customerHasError: PropTypes.bool.isRequired,
  patientHasError: PropTypes.bool.isRequired,
  isExternalInit: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  requestStatus: PropTypes.string.isRequired,
  setRef: PropTypes.func.isRequired,
  getRef: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  error: PropTypes.any,
  updatePermissions: PropTypes.object.isRequired,
  readPermissions: PropTypes.object.isRequired,
  isClosedAppointment: PropTypes.bool.isRequired,
  userIsOnCall: PropTypes.bool.isRequired,
};

const defaultProps = {
  fetchedBy: null,
  appointmentHasError: undefined,
  error: undefined,
  maxDate: moment().add(1, 'y').valueOf(),
};

const PickerToolbarComponent = ({ date, isLandscape }) => ( // eslint-disable-line
  <PickerToolbar isLandscape={ isLandscape }>
    <ToolbarButton
      variant="h4"
      selected
      label={ date.format('ddd, MMM D') } // eslint-disable-line
    />
  </PickerToolbar>
);

const EditAppointmentForm = ({
  intl,
  form,
  handleSubmit,
  submitForm,
  appointmentHasError,
  serviceConfig: {
    queueType,
    schedulingType,
    fixedAppointmentDuration,
    providerSelectionType,
  },
  providersStatus,
  providersOptions,
  isOngoingAppointment,
  fetchedBy,
  onProviderChange,
  cantChangeProviderDueToConversationStatus,
  conversationTypesOptions,
  onScheduledDateChange,
  initialValues: {
    status: appointmentStatusInitial,
    state: appointmentStateInitial,
    customerIsPatient,
  },
  timeSlotsOptions,
  onMonthChange,
  shouldDisableDate,
  shouldDisableEndDate,
  timeSlotsStatus,
  calendarTimeSlotsStatus,
  flexibleFromSlotsOptions,
  flexibleToSlotsOptions,
  onFlexibleFromChange,
  formatFlexibleInputChange,
  formValues,
  customerHasError,
  patientHasError,
  maxDate,
  untouch,
  changeFieldValue,
  appointmentStatusOptions,
  appointmentStateOptions,
  canEditStatus,
  isExternalInit,
  isMobile,
  requestStatus,
  setRef,
  getRef,
  error,
  onCancel,
  updatePermissions,
  readPermissions,
  isClosedAppointment,
  userIsOnCall,
}) => {
  const {
    date,
    endDate,
    status,
    flexible = {},
    providerUserId,
  } = formValues;

  return (
    <div className={ theme.root }>
      <div className={ theme.formWrap } ref={ setRef }>
        <form onSubmit={ handleSubmit }>
          <AppointmentAccordion
            defaultExpanded
            header={ isExpanded => (
              <div
                className={ theme.accordionHeaderWrap }
                aria-label="Appointment Details"
                onClick={ (event) => event.stopPropagation() }
                onFocus={ (event) => event.stopPropagation() }
              >
                <FormattedMessage
                  id="Patient.Appointment.Create.AppointmentDetails.Header"
                  defaultMessage="** Appointment details"
                />
                <If condition={ !isExpanded && appointmentHasError }>
                  <Icon name="exclamation-outline" className={ theme.iconError } />
                </If>
              </div>
            ) }
          >
            <div className={ theme.fieldsetWarp }>
              <Row>
                <Col xs={ 12 } md={ 6 } className={ theme.fieldset }>
                  <ArrowTooltip
                    title={ cantChangeProviderDueToConversationStatus && (schedulingType === TIME_SLOT || queueType === WALK_IN)
                      ? intl.formatMessage({
                        id: 'AdminConsole.Appointments.AppointmentDetails.EditDialog.ProviderName.AlertApptIsConnecting',
                        defaultMessage: '** Provider name can’t be updated while conversation status is connecting or in progress',
                      })
                      : intl.formatMessage({
                        id: 'AdminConsole.Appointments.Create.ProviderDisabled.Tooltip',
                        defaultMessage: '** Provider can\'t be updated while appointment is ongoing',
                      })
                    }
                    tooltipPlacement="bottom"
                    disabled={
                      (!isOngoingAppointment && schedulingType === FLEXIBLE)
                      || (fetchedBy && schedulingType === FLEXIBLE)
                      || (!cantChangeProviderDueToConversationStatus && (schedulingType === TIME_SLOT || queueType === WALK_IN))
                      || isClosedAppointment
                    }
                    isRtl={ isCurrentLanguageRtl() }
                    verticalOffset={ 10 }
                  >
                    <div>
                      <Field
                        name="providerUserId"
                        testId="providerUserId"
                        component={ !readPermissions.canSeeProvider ? RestrictedUserField : Autocomplete }
                        label={ appointmentFormFieldLabels.providerUserId }
                        labelSuffix={ providerSelectionType === OPTIONAL ? 'Optional' : undefined }
                        required={ providerSelectionType === REQUIRED }
                        items={ providersOptions }
                        viewportRef={ getRef }
                        onBlur={ () => { untouch('providerUserId'); } }
                        onChange={ onProviderChange }
                        clearable
                        loading={ providersStatus === requestStatuses.pending }
                        disabled={
                          providersStatus === requestStatuses.pending
                          || cantChangeProviderDueToConversationStatus
                          || providerSelectionType === DISABLED
                          || (isOngoingAppointment && schedulingType === FLEXIBLE)
                          || (providerSelectionType === REQUIRED && providersOptions.length <= 1)
                          || (fetchedBy && schedulingType === FLEXIBLE)
                          || !updatePermissions.canEditProvider
                          || isClosedAppointment
                          || userIsOnCall
                        }
                        errorMessagesMap={ errorMessagesMap }
                      />
                    </div>
                  </ArrowTooltip>
                </Col>
              </Row>

              <Row>
                <If condition={ queueType === SCHEDULED } >
                  <If condition={ schedulingType === TIME_SLOT }>
                    <Col xs={ 12 } md={ 6 } className={ theme.fieldset }>
                      <Field
                        id="date"
                        name="date"
                        testId="date"
                        component={ !readPermissions.canSeeSchedule ? RestrictedUserField : DateTimeInput }
                        label={ appointmentFormFieldLabels.date }
                        parse={ momentDate => momentDate.valueOf() }
                        onChange={ (value) => { onScheduledDateChange(value); } }
                        onBlur={ () => { untouch('date'); } }
                        disableTimeSelection
                        datepickerProps={{
                          disablePast: true,
                          onMonthChange,
                          shouldDisableDate,
                          maxDate,
                          ToolbarComponent: PickerToolbarComponent,
                        }}
                        disabled={
                          cantChangeProviderDueToConversationStatus
                          || (providerSelectionType === REQUIRED && !providerUserId)
                          || !updatePermissions.canEditSchedule
                          || isClosedAppointment
                          || userIsOnCall
                        }
                        errorMessagesMap={ errorMessagesMap }
                        loading={ calendarTimeSlotsStatus === requestStatuses.pending }
                        required
                      />
                    </Col>
                    <Col xs={ 12 } md={ 6 } className={ theme.fieldset }>
                      <Field
                        name="timeSlot"
                        testId="timeSlot"
                        component={ !readPermissions.canSeeSchedule ? RestrictedUserField : Select }
                        items={ timeSlotsOptions }
                        onBlur={ () => { untouch('timeSlot'); } }
                        label={ appointmentFormFieldLabels.timeSlot }
                        placeholder={{
                          id: 'AdminConsole.Appointments.Create.Dialog.DateAndTime.Time.Placeholder',
                          defaultMessage: '** hh:mm',
                        }}
                        disabled={
                          !date
                          || timeSlotsStatus === requestStatuses.pending
                          || !timeSlotsOptions.length
                          || cantChangeProviderDueToConversationStatus
                          || !updatePermissions.canEditSchedule
                          || isClosedAppointment
                          || userIsOnCall
                        }
                        loading={ timeSlotsStatus === requestStatuses.pending }
                        errorMessagesMap={ errorMessagesMap }
                        iconName="clock"
                        size="small"
                        clearable={ false }
                        required
                      />
                    </Col>
                  </If>
                  <If condition={ schedulingType === FLEXIBLE }>
                    <Col xs={ 12 } md={ 6 } className={ theme.fieldset }>
                      <div className={ theme.flexibleSlots }>
                        <Field
                          id="date"
                          name="date"
                          testId="date"
                          component={ !readPermissions.canSeeSchedule ? RestrictedUserField : DateTimeInput }
                          label={ appointmentFormFieldLabels.startDate }
                          parse={ momentDate => momentDate.valueOf() }
                          onChange={ (value) => { onScheduledDateChange(value, true, true); } }
                          onBlur={ () => { untouch('date'); } }
                          disableTimeSelection
                          datepickerProps={{
                            disablePast: true,
                            onMonthChange,
                            shouldDisableDate,
                            maxDate,
                            ToolbarComponent: PickerToolbarComponent,
                          }}
                          disabled={
                            appointmentStateInitial === ONGOING
                            || isOngoingAppointment
                            || (!providerUserId && providerSelectionType === REQUIRED)
                            || !updatePermissions.canEditSchedule
                            || isClosedAppointment
                            || userIsOnCall
                          }
                          errorMessagesMap={ errorMessagesMap }
                          loading={ calendarTimeSlotsStatus === requestStatuses.pending }
                          required
                        />
                        <Field
                          name="flexible.from"
                          testId="timeSlot"
                          component={ !readPermissions.canSeeSchedule ? RestrictedUserField : Autocomplete }
                          items={ flexibleFromSlotsOptions }
                          onChange={ onFlexibleFromChange }
                          onBlur={ () => { untouch('flexible.from'); } }
                          label={ appointmentFormFieldLabels.flexibleFrom }
                          placeholder={{
                            id: 'AdminConsole.Appointments.Create.Dialog.DateAndTime.Time.Placeholder',
                            defaultMessage: '** hh:mm',
                          }}
                          disabled={ !date
                            || timeSlotsStatus === requestStatuses.pending
                            || !timeSlotsOptions.length
                            || appointmentStateInitial === ONGOING
                            || isOngoingAppointment
                            || !updatePermissions.canEditSchedule
                            || isClosedAppointment
                            || userIsOnCall
                          }
                          loading={ timeSlotsStatus === requestStatuses.pending }
                          errorMessagesMap={ errorMessagesMap }
                          iconName="clock"
                          size="small"
                          formatInputChange={ formatFlexibleInputChange }
                          required
                        />
                      </div>
                    </Col>
                    <Col xs={ 12 } md={ 6 } className={ theme.fieldset }>
                      <div className={ theme.flexibleSlots }>
                        <Field
                          id="endDate"
                          name="endDate"
                          testId="endDate"
                          component={ !readPermissions.canSeeSchedule ? RestrictedUserField : DateTimeInput }
                          label={ appointmentFormFieldLabels.endDate }
                          parse={ momentDate => momentDate.valueOf() }
                          onChange={ (value) => { onScheduledDateChange(value, true); } }
                          onBlur={ () => { untouch('endDate'); } }
                          disableTimeSelection
                          datepickerProps={{
                            disablePast: true,
                            onMonthChange,
                            shouldDisableDate: shouldDisableEndDate,
                            maxDate,
                            ToolbarComponent: PickerToolbarComponent,
                          }}
                          disabled={
                            appointmentStateInitial === ONGOING
                            || isOngoingAppointment
                            || (!providerUserId && providerSelectionType === REQUIRED)
                            || !date
                            || !updatePermissions.canEditSchedule
                            || isClosedAppointment
                            || userIsOnCall
                          }
                          errorMessagesMap={ errorMessagesMap }
                          loading={ calendarTimeSlotsStatus === requestStatuses.pending }
                          required
                        />
                        <Field
                          name="flexible.to"
                          testId="timeSlot"
                          component={ !readPermissions.canSeeSchedule ? RestrictedUserField : Autocomplete }
                          items={ flexibleToSlotsOptions }
                          onBlur={ () => { untouch('flexible.to'); } }
                          label={ appointmentFormFieldLabels.flexibleTo }
                          placeholder={{
                            id: 'AdminConsole.Appointments.Create.Dialog.DateAndTime.Time.Placeholder',
                            defaultMessage: '** hh:mm',
                          }}
                          disabled={ !endDate || !flexible || (flexible && !flexible.from) || !!fixedAppointmentDuration
                            || timeSlotsStatus === requestStatuses.pending
                            || !timeSlotsOptions.length
                            || appointmentStateInitial === ONGOING
                            || isOngoingAppointment
                            || !updatePermissions.canEditSchedule
                            || isClosedAppointment
                            || userIsOnCall
                          }
                          errorMessagesMap={ errorMessagesMap }
                          iconName="clock"
                          size="small"
                          formatInputChange={ formatFlexibleInputChange }
                          required
                        />
                      </div>
                    </Col>
                  </If>
                </If>
              </Row>

              <Row>
                <Col xs={ 12 } md={ 6 } className={ theme.fieldset }>
                  <Field
                    name="conversationType"
                    testId="conversationType"
                    component={ Select }
                    label={ appointmentFormFieldLabels.conversationType }
                    placeholder={{
                      id: 'AdminConsole.Appointments.Create.Dialog.ConvType.Placeholder',
                      defaultMessage: '** Select conversation type',
                    }}
                    items={ conversationTypesOptions }
                    onBlur={ () => { untouch('conversationType'); } }
                    viewportRef={ getRef }
                    clearable={ false }
                    disabled={ isClosedAppointment }
                    required
                  />
                </Col>

                <Col xs={ 12 } md={ 6 } className={ theme.fieldset }>
                  <Field
                    name="status"
                    testId="status"
                    component={ Select }
                    items={ appointmentStatusOptions }
                    onBlur={ () => { untouch('status'); } }
                    onChange={ () => {
                      changeFieldValue(form, 'state', null);
                    } }
                    label={ appointmentFormFieldLabels.status }
                    size="small"
                    clearable={ false }
                    disabled={
                      !canEditStatus
                      || appointmentStatusInitial !== OPENED
                      || isClosedAppointment
                      || isOngoingAppointment
                    }
                    viewportRef={ getRef }
                    errorMessagesMap={ errorMessagesMap }
                  />
                </Col>

                <Col xs={ 12 } md={ 6 } className={ theme.fieldset }>
                  <Field
                    name="state"
                    testId="state"
                    component={ Select }
                    items={ appointmentStateOptions }
                    onBlur={ () => { untouch('state'); } }
                    label={ appointmentFormFieldLabels.state }
                    size="small"
                    clearable={ false }
                    disabled={
                      !canEditStatus
                      || status !== CANCELED
                      || !appointmentStateOptions.length
                      || isClosedAppointment
                      || isOngoingAppointment
                    }
                    viewportRef={ getRef }
                    errorMessagesMap={ errorMessagesMap }
                  />
                </Col>
              </Row>
            </div>
          </AppointmentAccordion>

          <AppointmentAccordion
            header={ (isExpanded) => (
              <div
                className={ theme.accordionHeaderWrap }
                aria-label="My details"
                onClick={ (event) => event.stopPropagation() }
                onFocus={ (event) => event.stopPropagation() }
              >
                <FormattedMessage
                  id="Patient.Appointment.Create.CustomerDetails.Header"
                  defaultMessage="** My details"
                />
                <If condition={ !isExpanded && customerHasError }>
                  <Icon name="exclamation-outline" className={ theme.iconError } />
                </If>
              </div>
            ) }
          >
            <div className={ theme.fieldsetWarp }>
              <PatientDetailsForm
                fieldName="customer"
                viewportRef={ getRef }
                formValues={ formValues }
                submitFormStatus={ requestStatus }
                disabled={ !updatePermissions.canEditCustomer || isClosedAppointment }
                readable={ readPermissions.canSeeCustomer }
              />
            </div>
          </AppointmentAccordion>

          <AppointmentAccordion
            header={ (isExpanded) => (
              <div
                className={ theme.accordionHeaderWrap }
                aria-label="My details"
                onClick={ (event) => event.stopPropagation() }
                onFocus={ (event) => event.stopPropagation() }
              >
                <FormattedMessage
                  id="Patient.Appointment.Create.PatientDetails.Header"
                  defaultMessage="** Patient details"
                />
                <If condition={ !isExpanded && patientHasError }>
                  <Icon name="exclamation-outline" className={ theme.iconError } />
                </If>
              </div>
            ) }
          >
            <div className={ theme.fieldsetWarp }>
              <PatientDetailsForm
                fieldName={ customerIsPatient ? 'customer' : 'patient' }
                disabled={ true }
                readable={ readPermissions.canSeeSubjects }
                viewportRef={ getRef }
                formValues={ formValues }
                submitFormStatus={ requestStatus }
              />
            </div>
          </AppointmentAccordion>
        </form>
        <If condition={ error }>
          <FormError error={ error } />
        </If>
      </div>
      <div className={ theme.footer }>
        <If condition={ !isExternalInit }>
          <CancelButton
            handleCancelClick={ onCancel }
            size={ isMobile ? 'small' : 'medium' }
            isLoading={ requestStatus === requestStatuses.pending }
            isDisabled={ requestStatus === requestStatuses.pending }
          />
        </If>
        <SaveButton
          handleClick={ submitForm }
          size={ isMobile ? 'small' : 'medium' }
          isLoading={ requestStatus === requestStatuses.pending }
          isDisabled={ requestStatus === requestStatuses.pending || isClosedAppointment }
          iconDontFlipOnRtl
        />
      </div>
    </div>
  );
};

EditAppointmentForm.propTypes = propTypes;
EditAppointmentForm.defaultProps = defaultProps;

export default reduxForm({
  touchOnBlur: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(EditAppointmentForm);
